'use client';

import { isInApp, isInVest } from '@/libs/comeAppSDK';
import { getAccessToken as getAccessTokenFromVest, getAppInstanceId } from '@/libs/comeAppSDK/adapt-index';
import { setAuthToken } from '@/utils/token';
import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { userAtom, tokenInitializedAtom } from '@/store';
import { loginPage } from '@/const/page-url';
import { useRouter } from '@/i18n/navigation';
import { events } from '@/libs/comeAppSDK/events';
import { addEventListener } from '@/libs/comeAppSDK';
import { REDIRECT_URL_KEY } from '@/const/storage-key';
import { pwaAutoLocationAtom, utmActAtom } from '@/store/attribute-uuid';
import { ACTIVITY_URL_MAP } from '@/const/activity-url-map';
import { useAttridBind } from '@/features/auth/services/attrid-bind';

// 模块级 in-flight 锁：跨 StrictMode 的卸载/重挂载也能生效
let tokenRequestInFlight: Promise<void> | null = null;

export default function TokenInitializer() {
  const [, setUser] = useAtom(userAtom);
  const [, setTokenInitialized] = useAtom(tokenInitializedAtom);
  const [utmAct] = useAtom(utmActAtom);
  const [pwaAutoLocation, setPwaAutoLocation] = useAtom(pwaAutoLocationAtom);
  const { mutateAsync: attridBind } = useAttridBind();

  const router = useRouter();

  // 马甲包归因调用
  const bindAttrid = async () => {
    try {
      const appInstanceId = await getAppInstanceId();
      console.log('bindAttrid', appInstanceId);
      if (appInstanceId) {
        const res = await attridBind({ params: { appInstanceId } });
        if (res?.routePath) {
          router.replace(res.routePath);
        }
      }
    } catch (err) {
      console.warn('[token-initializer] getAppInstanceId failed:', err);
    }
  };

  const getToken = async () => {
    if (tokenRequestInFlight) {
      await tokenRequestInFlight;
      return;
    }
    tokenRequestInFlight = (async () => {
      if (isInApp()) {
        const result = await getAccessTokenFromVest();
        const token = result?.token;
        setUser({ name: '', accessToken: token });
        await setAuthToken(token);
        // 没有token，跳转到登录页
        if (!token) {
          const redirectUrl = encodeURIComponent(window.location.href);
          console.log('获取redirectUrl', redirectUrl);
          localStorage.setItem(REDIRECT_URL_KEY, redirectUrl);
          console.log('redirectUrl存入缓存--->', localStorage.getItem(REDIRECT_URL_KEY));
          router.push(loginPage.login);
        } else {
          setTokenInitialized(true);
          // laowang团队，马甲包 归因跳转
          if (isInVest()) {
            bindAttrid();
          }
        }
      } else {
        // PWA保存到桌面的情况，首次做自动跳转，二次不需要（待pwa做了动态链接后，这里需要移除）

        // 新增子游戏的utm_act参数：g_{gameid}_{symbol}，如：utm_act=g_345_INR，链接示例：/casino/games/play?id=345&currency=INR
        // 新增Casino tab筛选项参数：t_{tab_uuid}，如：t_3543，链接示例：/casino?tab=3543，注：PWA跳转到：/casino?tab=3543
        // utmAct
        // 根据参数utmAct,如果是g_{gameid}_{symbol}，则跳转到/casino/games/play?id={gameid}&currency={symbol}
        // 如果是t_{tab_uuid}，则跳转到/casino?tab={tab_uuid}
        if (utmAct && !pwaAutoLocation) {
          let targetUrl = '';
          if (utmAct.startsWith('g_')) {
            const gameid = utmAct.split('_')[1];
            targetUrl = `/casino/games/detail?id=${gameid}`;
          } else if (utmAct.startsWith('t_')) {
            const tabUuid = utmAct.split('_')[1];
            targetUrl = `/casino?tab=${tabUuid}`;
          } else {
            const url = ACTIVITY_URL_MAP[utmAct];
            if (url) {
              targetUrl = url;
            }
          }

          if (targetUrl) {
            // 预取目标路由以加速跳转
            router.prefetch(targetUrl);
            // 使用 replace 而不是 push，因为这是初始化逻辑，不应该在历史记录中留下记录
            router.replace(targetUrl);
          }
          // 标记已经首次跳转
          setPwaAutoLocation('1');
          return; // 跳转后直接返回，不设置 tokenInitialized
        }
        setTokenInitialized(true);
      }
    })().finally(() => {
      tokenRequestInFlight = null;
    });

    await tokenRequestInFlight;
  };

  useEffect(() => {
    getToken();
  }, []);

  // 预加载登录页
  useEffect(() => {
    if (isInApp()) {
      router.prefetch(loginPage.login);
    }
  }, []);

  const handleAutoLoginError = () => {
    router.push(loginPage.login);
  };

  useEffect(() => {
    addEventListener(events.userNotLogedIn, handleAutoLoginError);
    addEventListener(events.userInvalid, handleAutoLoginError);
    return () => {
      removeEventListener(events.userNotLogedIn, handleAutoLoginError);
      removeEventListener(events.userInvalid, handleAutoLoginError);
    };
  }, []);

  return null;
}

